import { MutableRefObject } from "react";

import { createStore } from "zustand";

type BlockTarget = {
  ref: MutableRefObject<HTMLElement | null>;
  onScrollEndRef?: MutableRefObject<(() => void) | undefined>;
};

type DOMPosition = {
  top: number;
  left: number;
};

type CaseBlockScrollState = {
  registeredBlocks: Set<string>;
  lastFocusedBlock: string | null;
  visibleBlocks: Map<string, DOMPosition>;
};

type CaseBlockScrollActions = {
  registerBlock: (
    blockId: string,
    ref: MutableRefObject<HTMLElement | null>,
    onScrollEndRef?: MutableRefObject<(() => void) | undefined>,
  ) => void;
  unregisterBlock: (blockId: string) => void;
  scrollToBlock: (blockId: string) => void;
  unsetLastFocusedBlock: () => void;
  setBlockVisible: (
    blockId: string,
    isVisible: boolean,
    position: DOMPosition,
  ) => void;
};

type CaseBlockScrollStore = ZustandStore<
  CaseBlockScrollState,
  CaseBlockScrollActions
>;

export const createCaseBlockScrollStore = () => {
  // Store refs outside of Zustand to avoid immer issues
  const blockTargetsRef = new Map<string, BlockTarget>();

  return createStore<CaseBlockScrollStore>((set) => ({
    registeredBlocks: new Set(),
    lastFocusedBlock: null,
    visibleBlocks: new Map(),
    actions: {
      registerBlock: (blockId, ref, onScrollEndRef) => {
        blockTargetsRef.set(blockId, { ref, onScrollEndRef });
        set((state) => ({
          registeredBlocks: new Set([...state.registeredBlocks, blockId]),
        }));
      },
      unregisterBlock: (blockId) => {
        blockTargetsRef.delete(blockId);
        set((state) => {
          const newRegisteredBlocks = new Set(state.registeredBlocks);
          const newVisibleBlocks = new Map(state.visibleBlocks);
          newRegisteredBlocks.delete(blockId);
          newVisibleBlocks.delete(blockId);

          return {
            registeredBlocks: newRegisteredBlocks,
            visibleBlocks: newVisibleBlocks,
          };
        });
      },
      scrollToBlock: (blockId) => {
        const target = blockTargetsRef.get(blockId);

        if (!target?.ref.current) {
          // Block not found, silently return
          return;
        }

        // Update lastFocusedBlock immediately when scrolling starts
        set((state) => ({
          ...state,
          lastFocusedBlock: blockId,
        }));

        const element = target.ref.current;

        observeBlockInViewPort(element, () => {
          if (target.onScrollEndRef?.current) {
            target.onScrollEndRef.current();
          }
        });

        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      },
      unsetLastFocusedBlock: () => {
        set((state) => {
          if (state.lastFocusedBlock === null) {
            return state;
          }
          return {
            ...state,
            lastFocusedBlock: null,
          };
        });
      },
      setBlockVisible: (blockId, isVisible, position) => {
        set((state) => {
          const newVisibleBlocks = new Map(state.visibleBlocks);

          if (isVisible) {
            newVisibleBlocks.set(blockId, position);
          } else {
            newVisibleBlocks.delete(blockId);
          }

          return {
            ...state,
            visibleBlocks: newVisibleBlocks,
          };
        });
      },
    },
  }));
};

const observeBlockInViewPort = (
  element: HTMLElement,
  onComplete: () => void,
): void => {
  const observer = new IntersectionObserver(
    (entries) => {
      const [entry] = entries;
      if (entry.isIntersecting) {
        onComplete();
        observer.disconnect();
      }
    },
    {
      threshold: 0.8, // Trigger when at least 80% of the element is visible
    },
  );

  // Start observing the element (will fire immediately if already in view)
  observer.observe(element);
};
